<template>
  <div
    class="feature-card"
    :class="[
      `feature-card-${variant}`,
      { 'feature-card-large': large },
      { 'feature-card-hover': hoverable },
      customClass
    ]"
    @click="handleClick"
  >
    <!-- Card Header -->
    <div class="feature-card-header" v-if="showHeader">
      <div class="feature-icon-container" :class="`icon-bg-${iconColor}`">
        <SvgIcon :name="icon" :size="iconSize" :color="iconColor" class="feature-icon" />
      </div>
      <div v-if="badge" class="feature-badge">
        <span :class="`badge bg-${badgeColor}`">{{ badge }}</span>
      </div>
    </div>

    <!-- Card Body -->
    <div class="feature-card-body">
      <h3 class="feature-title" :class="`text-${titleColor}`">
        {{ title }}
      </h3>

      <p class="feature-description">
        {{ description }}
      </p>

      <!-- Feature List -->
      <ul v-if="features && features.length" class="feature-list">
        <li v-for="(feature, index) in features" :key="index" class="feature-list-item">
          <SvgIcon name="check-circle" size="sm" color="success" class="me-2" />
          {{ feature }}
        </li>
      </ul>

      <!-- Stats Display -->
      <div v-if="stats && stats.length" class="feature-stats">
        <div v-for="stat in stats" :key="stat.label" class="stat-item">
          <div class="stat-value" :class="`text-${stat.color || 'primary'}`">
            {{ stat.value }}
          </div>
          <div class="stat-label">{{ stat.label }}</div>
        </div>
      </div>
    </div>

    <!-- Card Footer -->
    <div class="feature-card-footer" v-if="showFooter">
      <button
        v-if="buttonText"
        class="btn"
        :class="`btn-${buttonVariant}`"
        @click="handleButtonClick"
      >
        <SvgIcon v-if="buttonIcon" :name="buttonIcon" size="sm" color="white" class="me-2" />
        {{ buttonText }}
      </button>

      <!-- Action Links -->
      <div v-if="actionLinks && actionLinks.length" class="action-links">
        <a
          v-for="link in actionLinks"
          :key="link.text"
          :href="link.href"
          class="action-link"
          :class="`text-${link.color || 'primary'}`"
          @click.prevent="handleLinkClick(link)"
        >
          <SvgIcon v-if="link.icon" :name="link.icon" size="sm" class="me-1" />
          {{ link.text }}
        </a>
      </div>
    </div>

    <!-- Overlay for interactive cards -->
    <div v-if="overlay" class="feature-overlay">
      <div class="overlay-content">
        <SvgIcon :name="overlayIcon || 'arrow-right'" size="lg" color="white" />
      </div>
    </div>

    <!-- Background decoration -->
    <div v-if="showDecoration" class="card-decoration" :class="`decoration-${variant}`"></div>
  </div>
</template>

<script>
import SvgIcon from './SvgIcon.vue'

export default {
  name: 'FeatureCard',
  components: {
    SvgIcon
  },
  props: {
    // Content Props
    title: {
      type: String,
      required: true
    },
    description: {
      type: String,
      required: true
    },
    icon: {
      type: String,
      default: 'lightbulb'
    },
    badge: {
      type: String,
      default: ''
    },

    // Styling Props
    variant: {
      type: String,
      default: 'primary',
      validator: (value) => ['primary', 'secondary', 'success', 'warning', 'info', 'light', 'dark'].includes(value)
    },
    iconColor: {
      type: String,
      default: 'primary'
    },
    titleColor: {
      type: String,
      default: 'dark'
    },
    badgeColor: {
      type: String,
      default: 'primary'
    },
    iconSize: {
      type: String,
      default: 'xl'
    },

    // Layout Props
    large: {
      type: Boolean,
      default: false
    },
    hoverable: {
      type: Boolean,
      default: true
    },
    showHeader: {
      type: Boolean,
      default: true
    },
    showFooter: {
      type: Boolean,
      default: false
    },
    showDecoration: {
      type: Boolean,
      default: true
    },
    overlay: {
      type: Boolean,
      default: false
    },
    overlayIcon: {
      type: String,
      default: 'arrow-right'
    },

    // Content Arrays
    features: {
      type: Array,
      default: () => []
    },
    stats: {
      type: Array,
      default: () => []
    },
    actionLinks: {
      type: Array,
      default: () => []
    },

    // Button Props
    buttonText: {
      type: String,
      default: ''
    },
    buttonIcon: {
      type: String,
      default: ''
    },
    buttonVariant: {
      type: String,
      default: 'primary'
    },

    // Custom Props
    customClass: {
      type: String,
      default: ''
    },
    href: {
      type: String,
      default: ''
    }
  },
  emits: ['click', 'button-click', 'link-click'],
  setup(props, { emit }) {
    const handleClick = () => {
      if (props.href) {
        window.location.href = props.href
      }
      emit('click')
    }

    const handleButtonClick = (event) => {
      event.stopPropagation()
      emit('button-click')
    }

    const handleLinkClick = (link) => {
      if (link.href) {
        if (link.external) {
          window.open(link.href, '_blank')
        } else {
          window.location.href = link.href
        }
      }
      emit('link-click', link)
    }

    return {
      handleClick,
      handleButtonClick,
      handleLinkClick
    }
  }
}
</script>

<style scoped>
.feature-card {
  position: relative;
  background: white;
  border-radius: 20px;
  padding: var(--spacing-xl);
  box-shadow: var(--shadow-md);
  border: 1px solid rgba(30, 58, 138, 0.1);
  transition: all 0.4s ease;
  overflow: hidden;
  cursor: default;
  height: 100%;
  display: flex;
  flex-direction: column;
  min-height: 320px;
}

.feature-card-hover {
  cursor: pointer;
}

.feature-card-hover:hover {
  transform: translateY(-8px);
  box-shadow: var(--shadow-xl);
  border-color: var(--idws-bright-blue);
}

.feature-card-large {
  padding: var(--spacing-2xl);
  border-radius: 24px;
}

/* Card Header */
.feature-card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: var(--spacing-lg);
}

.feature-icon-container {
  width: 80px;
  height: 80px;
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.feature-icon-container::before {
  content: '';
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: linear-gradient(45deg, transparent 30%, rgba(255, 255, 255, 0.2) 50%, transparent 70%);
  transform: translateX(-100%) translateY(-100%);
  transition: transform 0.6s ease;
}

.feature-card:hover .feature-icon-container::before {
  transform: translateX(100%) translateY(100%);
}

.icon-bg-primary {
  background: linear-gradient(135deg, var(--idws-blue-bg-light) 0%, var(--idws-blue-bg-soft) 100%);
  border: 2px solid var(--idws-bright-blue);
}

.icon-bg-success {
  background: linear-gradient(135deg, var(--idws-green-bg-light) 0%, var(--idws-green-bg-soft) 100%);
  border: 2px solid var(--idws-vibrant-green);
}

.icon-bg-warning {
  background: linear-gradient(135deg, var(--idws-yellow-bg-light) 0%, var(--idws-yellow-bg-soft) 100%);
  border: 2px solid var(--idws-golden-yellow);
}

.icon-bg-secondary {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border: 2px solid #6c757d;
}

.feature-icon {
  z-index: 2;
  position: relative;
}

.feature-badge {
  margin-top: var(--spacing-sm);
}

.feature-badge .badge {
  font-size: var(--font-size-sm);
  padding: var(--spacing-xs) var(--spacing-md);
  border-radius: 20px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Card Body */
.feature-card-body {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
}

.feature-title {
  font-size: var(--font-size-xl);
  font-weight: 700;
  margin-bottom: var(--spacing-md);
  line-height: 1.3;
  font-family: var(--font-primary);
}

.feature-card-large .feature-title {
  font-size: var(--font-size-2xl);
  margin-bottom: var(--spacing-lg);
}

.feature-description {
  font-size: var(--font-size-base);
  line-height: 1.6;
  color: var(--idws-text-muted);
  margin-bottom: var(--spacing-lg);
  flex-grow: 1;
}

.feature-card-large .feature-description {
  font-size: var(--font-size-lg);
  margin-bottom: var(--spacing-xl);
}

/* Feature List */
.feature-list {
  list-style: none;
  padding: 0;
  margin-bottom: var(--spacing-lg);
}

.feature-list-item {
  display: flex;
  align-items: center;
  margin-bottom: var(--spacing-sm);
  font-size: var(--font-size-base);
  color: var(--idws-text-dark);
}

.feature-list-item:last-child {
  margin-bottom: 0;
}

/* Stats Display */
.feature-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(80px, 1fr));
  gap: var(--spacing-md);
  margin-bottom: var(--spacing-lg);
  padding: var(--spacing-lg);
  background: var(--idws-blue-bg-light);
  border-radius: 12px;
  border: 1px solid var(--idws-blue-bg-soft);
}

.stat-item {
  text-align: center;
}

.stat-value {
  font-size: var(--font-size-2xl);
  font-weight: 700;
  line-height: 1;
  margin-bottom: var(--spacing-xs);
  font-family: var(--font-primary);
}

.stat-label {
  font-size: var(--font-size-sm);
  color: var(--idws-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 500;
}

/* Card Footer */
.feature-card-footer {
  margin-top: var(--spacing-lg);
  padding-top: var(--spacing-lg);
  border-top: 1px solid var(--idws-border-gray);
}

.action-links {
  display: flex;
  gap: var(--spacing-md);
  margin-top: var(--spacing-md);
  flex-wrap: wrap;
}

.action-link {
  text-decoration: none;
  font-weight: 500;
  display: flex;
  align-items: center;
  transition: all 0.3s ease;
  font-size: var(--font-size-base);
}

.action-link:hover {
  transform: translateX(4px);
  text-decoration: none;
}

/* Overlay */
.feature-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--idws-gradient-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: all 0.4s ease;
  border-radius: inherit;
}

.feature-card:hover .feature-overlay {
  opacity: 0.95;
}

.overlay-content {
  transform: scale(0.8);
  transition: transform 0.4s ease;
}

.feature-card:hover .overlay-content {
  transform: scale(1);
}

/* Background Decoration */
.card-decoration {
  position: absolute;
  width: 120px;
  height: 120px;
  border-radius: 50%;
  top: -60px;
  right: -60px;
  opacity: 0.1;
  transition: all 0.4s ease;
}

.decoration-primary {
  background: var(--idws-gradient-primary);
}

.decoration-success {
  background: var(--idws-gradient-secondary);
}

.decoration-warning {
  background: var(--idws-gradient-accent);
}

.feature-card:hover .card-decoration {
  opacity: 0.15;
  transform: scale(1.2);
}

/* Card Variants */
.feature-card-primary {
  border-color: var(--idws-bright-blue);
}

.feature-card-primary:hover {
  box-shadow: 0 12px 32px rgba(37, 99, 235, 0.25);
}

.feature-card-success {
  border-color: var(--idws-vibrant-green);
}

.feature-card-success:hover {
  box-shadow: 0 12px 32px rgba(34, 197, 94, 0.25);
}

.feature-card-warning {
  border-color: var(--idws-golden-yellow);
}

.feature-card-warning:hover {
  box-shadow: 0 12px 32px rgba(251, 191, 36, 0.25);
}

/* Responsive Design */
@media (max-width: 768px) {
  .feature-card {
    padding: var(--spacing-lg);
    min-height: 280px;
  }

  .feature-card-large {
    padding: var(--spacing-xl);
    min-height: 320px;
  }

  .feature-icon-container {
    width: 60px;
    height: 60px;
    border-radius: 16px;
  }

  .feature-title {
    font-size: var(--font-size-lg);
  }

  .feature-card-large .feature-title {
    font-size: var(--font-size-xl);
  }

  .feature-description {
    font-size: var(--font-size-base);
  }

  .feature-stats {
    grid-template-columns: repeat(2, 1fr);
    padding: var(--spacing-md);
  }

  .stat-value {
    font-size: var(--font-size-xl);
  }

  .action-links {
    flex-direction: column;
    gap: var(--spacing-sm);
  }
}

@media (max-width: 576px) {
  .feature-card {
    padding: var(--spacing-md);
    min-height: 240px;
  }

  .feature-stats {
    grid-template-columns: 1fr;
  }
}
</style>